import { useMutation, UseMutationResult, useQueryClient } from 'react-query';

import { getNewUser } from 'src/utils/urls';

import { createNewUser, NewUserReturnType } from '../types/createNewUser';

import { useAuth } from 'src/hooks';

type ApiErrorItem = {
  Code: string;
  FieldName: string;
  Message: string;
};

type ApiResponse<TData = unknown> = {
  Data?: TData;
  Errors?: ApiErrorItem[];
  ErrorId?: string;
  IsSuccess: boolean;
  StatusCode: number;
};

type TResult = ApiResponse<NewUserReturnType>;

type TError = Error;

type TVariables = createNewUser;

type TArguments = {
  token: string;
  tokenType: string;
  params: createNewUser;
};

async function saveUserRequest({
  token,
  tokenType,
  params,
}: TArguments): Promise<TResult> {
  const response = await fetch(getNewUser, {
    method: 'POST',
    headers: {
      Authorization: `${tokenType} ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(params),
  });

  let data: TResult | null = null;

  try {
    const text = await response.text();
    data = text ? JSON.parse(text) : null;
  } catch (e) {
    console.error('JSON parse failed:', e);
  }

  if (!data) {
    throw new Error('Invalid server response');
  }

  return data;
}

function useSaveUserDetails(): UseMutationResult<
  TResult,
  TError,
  TVariables,
  unknown
> {
  const queryClient = useQueryClient();
  const { token, tokenType } = useAuth();

  return useMutation(async (params: TVariables) => {
    const result = await saveUserRequest({
      token,
      tokenType,
      params,
    });

    if (!result.IsSuccess) {
      const errorMessage =
        result.Errors?.map(err => err.Message).join(', ') ||
        'Failed to create user';

      throw new Error(errorMessage);
    }

    queryClient.invalidateQueries(['get_users_list']);

    return result;
  });
}

export { useSaveUserDetails };
